// The instruction the app sends on the user's behalf when they add data to a session, plus the label
// the chat shows in its place.
//
// Pure and standalone by design: it lives next to the panel rather than in `utils/index.js` (which
// Vitest cannot parse — see "Vitest imports from src/utils" in frontend/CLAUDE.md), and it takes no
// hooks, so the wording can be asserted directly.
//
// Two variants, and the difference between them is the whole point. Uploading a file is an unambiguous
// statement of intent, so the FIRST one earns a full opening move: profile, read the destination's
// structure, propose a mapping. A LATER upload usually means "here is one more input for the thing we
// are already doing" — re-running the full move there would talk over a mapping the user is part-way
// through approving, so it profiles the new data and explicitly stops.

/** Frame keys the upload/import just registered, in a form safe to put in a sentence. */
function frameList(frames) {
  const keys = (frames || [])
    .map((f) => (typeof f === "string" ? f : f?.key))
    .filter((k) => typeof k === "string" && k.length > 0);
  // Deduplicated because a caller may pass the response's `dataframes` and `imports` arrays merged.
  return Array.from(new Set(keys));
}

function quoted(keys) {
  return keys.map((k) => `"${k}"`).join(", ");
}

/**
 * @param {object}   args
 * @param {Array}    args.frames       Registered frames, either `{key}` objects or plain key strings.
 * @param {string}   args.sourceName   File name or import name, for the user-facing label only.
 * @param {boolean}  args.isFirstTurn  True when the conversation has not started yet.
 * @param {string}   args.target       AiIngestTarget name, when the session is linked to one.
 * @returns {{text: string, display: string}} `text` goes to the model; `display` is shown instead.
 */
export function buildAutoAnalysePrompt({
  frames,
  sourceName = "",
  isFirstTurn = true,
  target = "",
} = {}) {
  const keys = frameList(frames);
  const named = keys.length > 0 ? quoted(keys) : "the data that was just added";
  const label = sourceName || (keys.length > 0 ? keys[0] : "the new data");

  if (!isFirstTurn) {
    return {
      display: `Profiling ${label}…`,
      text: [
        `I have just added more data to this session: ${named}.`,
        "",
        `Profile ${named} only — its columns, their types, and anything obviously wrong such as a`,
        "title row above the header, blank rows, or a totals row at the bottom. Say briefly how it",
        "relates to what we already have.",
        "",
        "Do NOT re-propose or rebuild a mapping we have already discussed, and do not modify any",
        "existing output DataFrame. If this new data changes something we agreed earlier, say so and",
        "wait for me rather than acting on it.",
      ].join("\n"),
    };
  }

  const steps = [
    `1. Profile ${named}: the columns, their types, and any problems worth flagging (a title row`,
    "   above the header, merged cells, blank rows, a stray totals row at the bottom, units that",
    "   look inconsistent).",
  ];

  if (target) {
    steps.push(
      `2. Call get_task_template for ${target} so you know exactly what the destination accepts.`,
      "3. Propose how my columns and values map onto it — including which values you are unsure",
      "   about — and then stop and wait for me to confirm.",
    );
  } else {
    steps.push(
      "2. Tell me what this data looks like it is for, and ask which part of the task I want to map",
      "   it into. Then stop and wait for me.",
    );
  }

  return {
    display: `Analysing ${label}…`,
    text: [
      `I have just uploaded ${named}. Make a start without waiting for me:`,
      "",
      ...steps,
      "",
      // The guard rail. Without it the model reads "make a start" as licence to transform data the
      // user has not agreed to, and a wrong guess is then work to unpick.
      "Do NOT create or modify any output DataFrame yet, and do not validate or commit anything.",
      "This turn is for understanding the data and agreeing a plan.",
    ].join("\n"),
  };
}

export default buildAutoAnalysePrompt;
